import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { InputGeneral } from "components/Input";
import Switch from "components/Switch";

import {
  ContainerContent,
  SwitchWrapper,
  InputWrapper,
  SectionLabel,
  Divider,
  WarningBanner,
  StringListBox,
  ChipItem,
  ChipRemoveButton,
  StringListLabel,
  StringListInputField,
} from "./styles";
import { DropdownGlobal } from "../../../../../components/DropdownGlobal";
import {
  loginConfigAttemptAux,
  loginConfigPeriodAttemptsAux,
} from "../../constants";

type AuthMode = "LOCAL" | "AD_LDAP" | "AZURE_AD";

interface LdapConfig {
  host: string;
  port: number;
  use_ssl: boolean;
  base_dn: string;
  login_attribute: string;
  service_user: string;
  service_pass: string;
  allowed_email_domains: string[];
  allowed_groups: string[];
}

interface AzureAdConfig {
  tenant_id: string;
  client_id: string;
  client_secret: string;
  authority_url: string;
  redirect_uri: string;
  allowed_email_domains: string[];
}

interface SecurityConfig {
  enable_mfa: boolean;
}

interface UserProvisioningConfig {
  auto_provision: boolean;
}

interface LoginConfig {
  Active: boolean;
  Attempt: number;
  PeriodAttempts: number;
  BlockTime: number;
  auth_mode: AuthMode;
  ldap: LdapConfig;
  azure_ad: AzureAdConfig;
  security: SecurityConfig;
  user_provisioning: UserProvisioningConfig;
}

const defaultConfig: LoginConfig = {
  Active: false,
  Attempt: 3,
  PeriodAttempts: 10,
  BlockTime: 10,
  auth_mode: "LOCAL",
  ldap: {
    host: "",
    port: 389,
    use_ssl: false,
    base_dn: "",
    login_attribute: "sAMAccountName",
    service_user: "",
    service_pass: "",
    allowed_email_domains: [],
    allowed_groups: [],
  },
  azure_ad: {
    tenant_id: "",
    client_id: "",
    client_secret: "",
    authority_url: "",
    redirect_uri: "",
    allowed_email_domains: [],
  },
  security: { enable_mfa: false },
  user_provisioning: { auto_provision: false },
};

interface LoginConfigurationCustomProps {
  editedValue: string;
  setEditedValue: (e: any) => void;
}

const AUTH_MODE_TO_ID: Record<AuthMode, number> = {
  LOCAL: 0,
  AD_LDAP: 1,
  AZURE_AD: 2,
};

const ID_TO_AUTH_MODE: Record<number, AuthMode> = {
  0: "LOCAL",
  1: "AD_LDAP",
  2: "AZURE_AD",
};

const LOGIN_ATTRIBUTE_OPTIONS = [
  { name: "sAMAccountName", id: "sAMAccountName" },
  { name: "userPrincipalName", id: "userPrincipalName" },
  { name: "mail", id: "mail" },
];

const StringListInput = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addItem = () => {
    const trimmed = inputValue.trim().replace(/,$/, "");
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue("");
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <InputWrapper>
      <StringListLabel>{label}</StringListLabel>
      <StringListBox
        focused={focused}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((item, index) => (
          <ChipItem key={index}>
            {item}
            <ChipRemoveButton onClick={() => removeItem(index)}>
              ×
            </ChipRemoveButton>
          </ChipItem>
        ))}
        <StringListInputField
          ref={inputRef}
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setInputValue(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addItem();
            }
            if (e.key === "Backspace" && !inputValue && value.length > 0) {
              removeItem(value.length - 1);
            }
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            addItem();
          }}
          placeholder={value.length === 0 ? placeholder : ""}
        />
      </StringListBox>
    </InputWrapper>
  );
};

const LoginConfigurationCustom = ({
  editedValue,
  setEditedValue,
}: LoginConfigurationCustomProps) => {
  const { t } = useTranslation();

  let parsedConfig: LoginConfig = defaultConfig;
  try {
    parsedConfig = editedValue
      ? { ...defaultConfig, ...JSON.parse(editedValue) }
      : defaultConfig;
    if (!parsedConfig.ldap) parsedConfig.ldap = defaultConfig.ldap;
    if (!parsedConfig.azure_ad) parsedConfig.azure_ad = defaultConfig.azure_ad;
    if (!parsedConfig.security) parsedConfig.security = defaultConfig.security;
    if (!parsedConfig.user_provisioning)
      parsedConfig.user_provisioning = defaultConfig.user_provisioning;
  } catch {
    parsedConfig = defaultConfig;
  }

  const initialConfig = parsedConfig;

  // Local fields
  const [loginConfigActive, setLoginConfigActive] = useState(
    initialConfig.Active,
  );
  const [loginConfigAttempt, setLoginConfigAttempt] = useState(
    initialConfig.Attempt,
  );
  const [loginConfigPeriodAttempts, setLoginConfigPeriodAttempts] = useState(
    initialConfig.PeriodAttempts,
  );
  const [loginConfigBlockTime, setLoginConfigBlockTime] = useState(
    initialConfig.BlockTime,
  );

  // Auth mode
  const [authMode, setAuthMode] = useState<AuthMode>(
    initialConfig.auth_mode ?? "LOCAL",
  );

  // AD LDAP fields
  const [ldapHost, setLdapHost] = useState(initialConfig.ldap.host);
  const [ldapPort, setLdapPort] = useState(initialConfig.ldap.port);
  const [ldapUseSsl, setLdapUseSsl] = useState(initialConfig.ldap.use_ssl);
  const [ldapBaseDn, setLdapBaseDn] = useState(initialConfig.ldap.base_dn);
  const [ldapLoginAttribute, setLdapLoginAttribute] = useState(
    initialConfig.ldap.login_attribute,
  );
  const [ldapServiceUser, setLdapServiceUser] = useState(
    initialConfig.ldap.service_user,
  );
  const [ldapServicePass, setLdapServicePass] = useState(
    initialConfig.ldap.service_pass,
  );
  const [ldapAllowedDomains, setLdapAllowedDomains] = useState<string[]>(
    initialConfig.ldap.allowed_email_domains ?? [],
  );
  const [ldapAllowedGroups, setLdapAllowedGroups] = useState<string[]>(
    initialConfig.ldap.allowed_groups ?? [],
  );

  // Azure AD fields
  const [azureTenantId, setAzureTenantId] = useState(
    initialConfig.azure_ad.tenant_id,
  );
  const [azureClientId, setAzureClientId] = useState(
    initialConfig.azure_ad.client_id,
  );
  const [azureClientSecret, setAzureClientSecret] = useState(
    initialConfig.azure_ad.client_secret,
  );
  const [azureAuthorityUrl, setAzureAuthorityUrl] = useState(
    initialConfig.azure_ad.authority_url,
  );
  const [azureRedirectUri, setAzureRedirectUri] = useState(
    initialConfig.azure_ad.redirect_uri,
  );
  const [azureAllowedDomains, setAzureAllowedDomains] = useState<string[]>(
    initialConfig.azure_ad.allowed_email_domains ?? [],
  );

  // Shared policies
  const [enableMfa, setEnableMfa] = useState(initialConfig.security.enable_mfa);
  const [autoProvision, setAutoProvision] = useState(
    initialConfig.user_provisioning.auto_provision,
  );

  const authTypeOptions = [
    { name: t("loginConfig.authTypeLocal"), id: 0 },
    { name: t("loginConfig.authTypeADLDAP"), id: 1 },
    { name: t("loginConfig.authTypeAzureAD"), id: 2 },
  ];

  const handleSslToggle = () => {
    const newSsl = !ldapUseSsl;
    setLdapUseSsl(newSsl);
    if (newSsl && ldapPort === 389) setLdapPort(636);
    if (!newSsl && ldapPort === 636) setLdapPort(389);
  };

  const updateConfigString = () => {
    const base = {
      Active: loginConfigActive,
      Attempt: loginConfigAttempt,
      PeriodAttempts: loginConfigPeriodAttempts,
      BlockTime: loginConfigBlockTime,
      auth_mode: authMode,
    };

    if (authMode === "LOCAL") {
      setEditedValue(JSON.stringify(base));
      return;
    }

    if (authMode === "AD_LDAP") {
      setEditedValue(
        JSON.stringify({
          ...base,
          ldap: {
            host: ldapHost,
            port: ldapPort,
            use_ssl: ldapUseSsl,
            base_dn: ldapBaseDn,
            login_attribute: ldapLoginAttribute,
            service_user: ldapServiceUser,
            service_pass: ldapServicePass,
            allowed_email_domains: ldapAllowedDomains,
            allowed_groups: ldapAllowedGroups,
          },
          security: { enable_mfa: enableMfa },
          user_provisioning: { auto_provision: autoProvision },
        }),
      );
      return;
    }

    if (authMode === "AZURE_AD") {
      setEditedValue(
        JSON.stringify({
          ...base,
          azure_ad: {
            tenant_id: azureTenantId,
            client_id: azureClientId,
            client_secret: azureClientSecret,
            authority_url: azureAuthorityUrl,
            redirect_uri: azureRedirectUri,
            allowed_email_domains: azureAllowedDomains,
          },
          security: { enable_mfa: enableMfa },
          user_provisioning: { auto_provision: autoProvision },
        }),
      );
    }
  };

  useEffect(() => {
    updateConfigString();
  }, [
    loginConfigActive,
    loginConfigAttempt,
    loginConfigPeriodAttempts,
    loginConfigBlockTime,
    authMode,
    ldapHost,
    ldapPort,
    ldapUseSsl,
    ldapBaseDn,
    ldapLoginAttribute,
    ldapServiceUser,
    ldapServicePass,
    azureTenantId,
    azureClientId,
    azureClientSecret,
    azureAuthorityUrl,
    azureRedirectUri,
    azureAllowedDomains,
    ldapAllowedDomains,
    ldapAllowedGroups,
    enableMfa,
    autoProvision,
  ]);

  return (
    <ContainerContent>
      <Switch
        dataTest="switch_login_config_active"
        onChange={() => setLoginConfigActive(!loginConfigActive)}
        checked={loginConfigActive}
        label={t("common.active")}
      />

      <DropdownGlobal
        labelName={t("loginConfig.authType")}
        options={authTypeOptions}
        error={false}
        setSelected={(id: number) =>
          setAuthMode(ID_TO_AUTH_MODE[id] ?? "LOCAL")
        }
        required={false}
        editDropdown={authTypeOptions[AUTH_MODE_TO_ID[authMode]]?.name}
        defaultValue={authTypeOptions[AUTH_MODE_TO_ID[authMode]]?.name}
      />

      <DropdownGlobal
        labelName={t("loginConfig.numberOfAttempts")}
        options={loginConfigAttemptAux}
        error={false}
        setSelected={(e: any) => setLoginConfigAttempt(e)}
        required={true}
        editDropdown={initialConfig.Attempt}
        defaultValue={String(initialConfig.Attempt)}
      />
      <DropdownGlobal
        labelName={t("loginConfig.attemptPeriod")}
        options={loginConfigPeriodAttemptsAux}
        error={false}
        setSelected={(e: any) => setLoginConfigPeriodAttempts(e)}
        required={false}
        editDropdown={initialConfig.PeriodAttempts}
        defaultValue={String(initialConfig.PeriodAttempts)}
      />
      <DropdownGlobal
        labelName={t("loginConfig.blockTime")}
        options={loginConfigPeriodAttemptsAux}
        error={false}
        setSelected={(e: any) => setLoginConfigBlockTime(e)}
        required={false}
        editDropdown={initialConfig.BlockTime}
        defaultValue={String(initialConfig.BlockTime)}
      />

      {authMode === "AD_LDAP" && (
        <>
          <Divider />
          <WarningBanner>{t("loginConfig.adWarning")}</WarningBanner>

          <SectionLabel>{t("loginConfig.sectionConnection")}</SectionLabel>
          <InputWrapper>
            <InputGeneral
              id="ldap-host"
              label={t("loginConfig.adHost")}
              value={ldapHost}
              onChange={(v) => setLdapHost(String(v))}
              required={true}
              error={false}
              width={381}
            />
          </InputWrapper>
          <InputWrapper>
            <InputGeneral
              id="ldap-port"
              label={t("loginConfig.adPort")}
              value={String(ldapPort)}
              onChange={(v) => setLdapPort(Number(v) || 389)}
              required={true}
              error={false}
              type="number"
              width={381}
            />
          </InputWrapper>
          <SwitchWrapper>
            <Switch
              dataTest="switch_ldap_ssl"
              onChange={handleSslToggle}
              checked={ldapUseSsl}
              label={t("loginConfig.adSsl")}
            />
          </SwitchWrapper>

          <SectionLabel>{t("loginConfig.sectionScope")}</SectionLabel>
          <InputWrapper>
            <InputGeneral
              id="ldap-base-dn"
              label={t("loginConfig.adBaseDn")}
              value={ldapBaseDn}
              onChange={(v) => setLdapBaseDn(String(v))}
              required={true}
              error={false}
              width={381}
              placeholder="OU=Usuarios,DC=empresa,DC=local"
            />
          </InputWrapper>
          <DropdownGlobal
            labelName={t("loginConfig.adLoginAttribute")}
            options={LOGIN_ATTRIBUTE_OPTIONS}
            error={false}
            setSelected={(id: string) => setLdapLoginAttribute(String(id))}
            required={false}
            editDropdown={ldapLoginAttribute}
            defaultValue={ldapLoginAttribute}
          />

          <SectionLabel>{t("loginConfig.sectionCredentials")}</SectionLabel>
          <InputWrapper>
            <InputGeneral
              id="ldap-service-user"
              label={t("loginConfig.adServiceUser")}
              value={ldapServiceUser}
              onChange={(v) => setLdapServiceUser(String(v))}
              required={true}
              error={false}
              width={381}
            />
          </InputWrapper>
          <InputWrapper>
            <InputGeneral
              id="ldap-service-pass"
              label={t("loginConfig.adServicePass")}
              value={ldapServicePass}
              onChange={(v) => setLdapServicePass(String(v))}
              required={true}
              error={false}
              width={381}
            />
          </InputWrapper>

          <StringListInput
            label={t("loginConfig.allowedEmailDomains")}
            value={ldapAllowedDomains}
            onChange={setLdapAllowedDomains}
            placeholder={t("loginConfig.allowedEmailDomainsPlaceholder")}
          />
          <StringListInput
            label={t("loginConfig.allowedGroups")}
            value={ldapAllowedGroups}
            onChange={setLdapAllowedGroups}
            placeholder={t("loginConfig.allowedGroupsPlaceholder")}
          />

          <SectionLabel>{t("loginConfig.sectionPolicies")}</SectionLabel>
          <SwitchWrapper>
            <Switch
              dataTest="switch_ad_auto_provision"
              onChange={() => setAutoProvision((p) => !p)}
              checked={autoProvision}
              label={t("loginConfig.adAutoProvision")}
            />
          </SwitchWrapper>
          <SwitchWrapper>
            <Switch
              dataTest="switch_ad_require_mfa"
              onChange={() => setEnableMfa((p) => !p)}
              checked={enableMfa}
              label={t("loginConfig.adRequireMfa")}
            />
          </SwitchWrapper>
        </>
      )}

      {authMode === "AZURE_AD" && (
        <>
          <Divider />
          <WarningBanner>{t("loginConfig.adWarning")}</WarningBanner>

          <SectionLabel>{t("loginConfig.sectionAzureConfig")}</SectionLabel>
          <InputWrapper>
            <InputGeneral
              id="azure-tenant-id"
              label={t("loginConfig.azureTenantId")}
              value={azureTenantId}
              onChange={(v) => setAzureTenantId(String(v))}
              required={true}
              error={false}
              width={381}
            />
          </InputWrapper>
          <InputWrapper>
            <InputGeneral
              id="azure-client-id"
              label={t("loginConfig.azureClientId")}
              value={azureClientId}
              onChange={(v) => setAzureClientId(String(v))}
              required={true}
              error={false}
              width={381}
            />
          </InputWrapper>
          <InputWrapper>
            <InputGeneral
              id="azure-client-secret"
              label={t("loginConfig.azureClientSecret")}
              value={azureClientSecret}
              onChange={(v) => setAzureClientSecret(String(v))}
              required={true}
              error={false}
              width={381}
            />
          </InputWrapper>
          <InputWrapper>
            <InputGeneral
              id="azure-authority-url"
              label={t("loginConfig.azureAuthorityUrl")}
              value={azureAuthorityUrl}
              onChange={(v) => setAzureAuthorityUrl(String(v))}
              required={false}
              error={false}
              width={381}
            />
          </InputWrapper>
          <InputWrapper>
            <InputGeneral
              id="azure-redirect-uri"
              label={t("loginConfig.azureRedirectUri")}
              value={azureRedirectUri}
              onChange={(v) => setAzureRedirectUri(String(v))}
              required={false}
              error={false}
              width={381}
            />
          </InputWrapper>

          <StringListInput
            label={t("loginConfig.allowedEmailDomains")}
            value={azureAllowedDomains}
            onChange={setAzureAllowedDomains}
            placeholder={t("loginConfig.allowedEmailDomainsPlaceholder")}
          />

          <SectionLabel>{t("loginConfig.sectionPolicies")}</SectionLabel>
          <SwitchWrapper>
            <Switch
              dataTest="switch_azure_auto_provision"
              onChange={() => setAutoProvision((p) => !p)}
              checked={autoProvision}
              label={t("loginConfig.azureAutoProvision")}
            />
          </SwitchWrapper>
          <SwitchWrapper>
            <Switch
              dataTest="switch_azure_require_mfa"
              onChange={() => setEnableMfa((p) => !p)}
              checked={enableMfa}
              label={t("loginConfig.azureRequireMfa")}
            />
          </SwitchWrapper>
        </>
      )}
    </ContainerContent>
  );
};

export default LoginConfigurationCustom;
